import type { Mesh } from "@babylonjs/core/Meshes/mesh";
import type { RacingState } from "@townsy/contracts";
import { RACE_HORSE_COUNT } from "@townsy/contracts";
import { RACE_SEATS, raceCoursePoint, raceLaneOffset } from "@townsy/world";
import type { VenueSceneContext, VenueSceneHandle, VenueSeatPose } from "../types";
import { SURFACE_Y, buildCourse, createRaceMaterials } from "./course3d";
import { HORSE_NOSE, HorseRig } from "./horses3d";
import { asRacingState, liveOrder, secondsLeft } from "./model";
import { paintTote } from "./textures";
import { RaceTimeline } from "./timeline";

/** Placeholder look for the stalls before the first state arrives. */
const BLANK_HORSE = { coat: "#7B4A2B", silk: { body: "#1F6B47", trim: "#F4ECDA", pattern: "solid" as const } };

/**
 * Townsy Downs 3D (ADR-0016): the racecourse, the eight horses and the tote board. Horses line up at the start while
 * betting is open, run from the server's positions (10 Hz, blended on the race clock by RaceTimeline) and pull up
 * past the line; the tote board shows the card, the countdown, the running order and the result. Seated visitors are
 * drawn sitting in their grandstand seats (sitAt). Only displays the server's state.
 */
export function buildRaceTrack3d(ctx: VenueSceneContext): VenueSceneHandle & { debug(): Record<string, unknown> } {
  const { scene } = ctx;
  const mats = createRaceMaterials(scene);
  const course = buildCourse(scene, mats);
  const horses: HorseRig[] = [];
  for (let n = 1; n <= RACE_HORSE_COUNT; n++) horses.push(new HorseRig(scene, n, mats.vc, mats.numbers, BLANK_HORSE));
  const casters = (): Mesh[] => [...course.casters, ...horses.flatMap((h) => h.meshes)];
  ctx.addShadowCasters(casters());

  const timeline = new RaceTimeline();
  let state: RacingState | null = null;
  let receivedAt = 0;
  let seats = new Map<string, number>();
  let toteKey = "";
  let lastTotePaint = 0;

  const repaintTote = (now: number, force: boolean) => {
    const secs = state ? secondsLeft(state, receivedAt, now) : 0;
    const key = state
      ? `${state.raceId}|${state.phase}|${state.bettingOpen}|${secs}|${state.finish.length}|${state.result?.raceId ?? ""}|${state.phase === "racing" ? liveOrder(state).join("") : ""}|${state.horses.map((h) => h.odds.join("-")).join(",")}`
      : "none";
    if (key === toteKey) return;
    // During the race the running order may change often: at most ~3 repaints a second (the texture is large).
    if (!force && state?.phase === "racing" && now - lastTotePaint < 330) return;
    toteKey = key;
    lastTotePaint = now;
    paintTote(mats.toteTexture, state, secs);
  };

  const place = (dtSeconds: number, now: number) => {
    const sample = timeline.sample(now);
    for (const h of horses) {
      const i = h.n - 1;
      const m = sample ? (sample.m[i] ?? 0) : 0;
      const v = sample ? (sample.v[i] ?? 0) : 0;
      const p = raceCoursePoint(m - HORSE_NOSE, raceLaneOffset(h.n));
      h.update(dtSeconds, p.x, SURFACE_Y, p.z, p.heading, v);
    }
  };
  place(0, performance.now());

  return {
    update(raw) {
      const next = asRacingState(raw);
      if (!next) return;
      const now = performance.now();
      const fieldChanged = !state || state.raceId !== next.raceId;
      state = next;
      receivedAt = Date.now();
      timeline.push(next, now);
      if (fieldChanged) {
        const rebuilt = next.horses.map((h) => horses[h.n - 1]).filter((rig, i) => rig?.setLook(next.horses[i]!) === true);
        if (rebuilt.length > 0) ctx.addShadowCasters(rebuilt.flatMap((h) => h!.meshes));
      }
      course.gate?.setEnabled(next.phase === "betting");
      seats = new Map(next.seats.map(([seat, actorId]) => [actorId, seat]));
      repaintTote(Date.now(), true);
    },
    frame(dtMs, nowMs) {
      place(Math.min(0.1, dtMs / 1000), nowMs);
      repaintTote(Date.now(), false);
    },
    sitAt(actorId): VenueSeatPose | null {
      const index = seats.get(actorId);
      const seat = index === undefined ? undefined : RACE_SEATS[index];
      return seat ? { x: seat.x, y: seat.y, z: seat.z, facing: seat.facing } : null;
    },
    debug() {
      return {
        raceId: state?.raceId ?? null,
        phase: state?.phase ?? null,
        horses: horses.map((h) => ({ n: h.n, x: h.root.position.x, z: h.root.position.z })),
        gate: course.gate?.isEnabled() ?? false,
        seats: [...seats.entries()],
      };
    },
    dispose() {
      for (const h of horses) h.dispose();
      course.dispose();
      mats.dispose();
    },
  };
}
